import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { Typography, Box } from '@mui/material';
import { CreateAccountHolderInfo } from 'common/types/adyen';
import Spinner from 'components/Spinner';
import { getAdyenFieldErrors, FieldErrors, fetchOnboardingUrl } from '../../utils';
import { newFirestoreId } from 'common/utils/newRentalUtils';
import errorHandler from 'common/services/errorHandler';
import { HOP_CURRENCY_CODES, HopCurrencyCode } from 'common/constants/currencies';
import { ADYEN_HOP_COUNTRY_CODES } from 'common/constants/countries';
import { createAccountHolder } from 'common/api/db/functions/callable';
import { useTranslation } from 'services/localization/useTranslation';
import * as IntercomService from 'services/intercom';
import * as ShopSelectors from 'selectors/ShopSelectors';
import { withRetry } from 'common/utils/common';
import BackdropDialog from 'common/components/BackdropDialog';
import { useForm } from 'react-hook-form';
import { PaymentInfoForm } from './PaymentInfoForm';
import { ManualRedirect } from './ManualRedirect';
import Banner from 'components/Banner';

export type PaymentFormValues = {
	legalName: string;
	email: string;
	country: string;
	currency: HopCurrencyCode | typeof OTHER_CURRENCY;
};

interface Props {
	open: boolean;
	onClose: () => void;
}

export const OTHER_COUNTRY = '__OTHER_COUNTRY__';
export const OTHER_CURRENCY = '__OTHER_CURRENCY__';

export const PaymentSetupDialog = (props: Props) => {
	const { t } = useTranslation();
	const shop = useSelector(ShopSelectors.activeShopData)!;
	const { name, email: shopEmail, country: shopCountry } = shop.publicInfo;
	const shopCountrySupported =
		!!shopCountry && ADYEN_HOP_COUNTRY_CODES.some((code) => code === shopCountry);

	const [loadingHop, setLoadingHop] = useState<boolean>(false);
	const [hopUrl, setHopUrl] = useState<string | null>(null);
	const [accountCreated, setAccountCreated] = useState<boolean>(false);
	const [formSendError, setFormSendError] = useState<string>('');

	const form = useForm<PaymentFormValues>({
		defaultValues: {
			legalName: name ?? '',
			email: shopEmail ?? '',
			country: shopCountrySupported ? shopCountry! : '',
			currency:
				HOP_CURRENCY_CODES.find((code) => code === shop.publicInfo.currency?.code) ??
				HOP_CURRENCY_CODES[0],
		},
	});

	const { isSubmitting } = form.formState;
	const { country, currency, legalName, email } = form.watch();
	const isOtherCountry = country === OTHER_COUNTRY;
	const validCountry = !!country && !isOtherCountry;
	const isOtherCurrency = currency === OTHER_CURRENCY;
	const validCurrency = !!currency && !isOtherCurrency;
	const validBusinessName = !!legalName;
	const validEmail = !!email;
	const validForm = validCountry && validCurrency && validBusinessName && validEmail;

	const onSubmit = async (formData: PaymentFormValues) => {
		setFormSendError('');
		const accountHolderInfo: CreateAccountHolderInfo = {
			accountHolderCode: newFirestoreId(),
			accountHolderDetails: {
				address: {
					country: formData.country,
				},
				bankAccountDetails: [],
				businessDetails: {
					legalBusinessName: formData.legalName,
					shareholders: [],
				},
				email: formData.email,
			},
			createDefaultAccount: true,
			legalEntity: 'Business',
			primaryCurrency: formData.currency,
			processingTier: 3,
		};
		try {
			const response = await createAccountHolder({
				shopId: shop.shopInfo.id,
				info: accountHolderInfo,
			});
			const accountHolderCode: string = response.accountHolderCode;
			setAccountCreated(true);
			createOnboardingLink(accountHolderCode);
		} catch (error) {
			errorHandler.report(error);
			if (error.details && error.details.invalidFields) {
				const errors = getAdyenFieldErrors(error.details.invalidFields);
				setFormErrors(errors);
			}
			setFormSendError('Error' + (error.message ? `: ${error.message}` : ''));
		}
	};

	const setFormErrors = (fieldErrors: FieldErrors) => {
		const companyErrors = fieldErrors.company || {};
		if (!!companyErrors.legalBusinessName) {
			form.setError('legalName', {
				message: companyErrors.legalBusinessName,
			});
		}
		if (!!companyErrors.email) {
			form.setError('email', {
				message: companyErrors.email,
			});
		}
		if (!!companyErrors.country) {
			form.setError('country', {
				message: companyErrors.country,
			});
		}
	};

	const createOnboardingLink = async (accountHolderCode: string) => {
		setLoadingHop(true);
		try {
			const { hopUrl } = await withRetry(() => fetchOnboardingUrl(accountHolderCode), {
				maxRetryCount: 5,
			});
			setHopUrl(hopUrl);
			window.location.href = hopUrl;
		} catch (e) {
			errorHandler.report(e);
		}
		setLoadingHop(false);
	};

	const showManualRedirect = accountCreated && !loadingHop;

	return (
		<BackdropDialog
			isOpen={props.open}
			onClose={props.onClose}
			label="Rentle Payments"
			body={
				loadingHop ? (
					<LoadingState
						text={t('shop.redirectToPaymentInfo', 'Redirecting to complete accout setup')}
					/>
				) : showManualRedirect ? (
					<ManualRedirect url={hopUrl ?? ''} closeView={props.onClose} />
				) : (
					<>
						<PaymentInfoForm
							control={form.control}
							formState={form.formState}
							formSendError={formSendError}
						/>
						<Box mb={4} />
						{isOtherCountry || isOtherCurrency ? (
							<NoRentlePaymentsSupportNote />
						) : (
							<AdyenRedirectNote />
						)}
					</>
				)
			}
			variant="primary"
			zIndex={1300}
			confirm={
				showManualRedirect || loadingHop
					? undefined
					: {
							label: t('account.payments.continueToVerification', 'Continue to verification'),
							closeAfterDone: false,
							disabled: !validForm || isSubmitting || loadingHop,
							callback: form.handleSubmit(onSubmit),
							disableAutoClose: true,
							loading: isSubmitting,
					  }
			}
			cancel={{
				label: t('common:actions.cancel'),
				disabled: isSubmitting,
			}}
		/>
	);
};

const LoadingState = ({ text }: { text?: string }) => {
	return <Spinner text={text} />;
};

const AdyenRedirectNote = () => {
	const { t } = useTranslation();
	return (
		<Typography variant="body2" color="textSecondary">
			{t(
				'account.payments.adyenRedirectNote',
				'You’ll be directed to our payment partner Adyen to continue the verification.',
			)}
		</Typography>
	);
};

const NoRentlePaymentsSupportNote = () => {
	const { t } = useTranslation();
	return (
		<Banner
			variant="warning"
			title={t(
				'account.payments.noRentlePaymentSupport',
				'Rentle Payments is not yet supported in your location. Please contact us to know when Rentle Payments would be available.',
			)}
			button={{
				text: t('common:actions.contactUs'),
				onClick: () => IntercomService.open(),
			}}
		/>
	);
};
